import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  ShieldCheck, ShieldWarning, XCircle, Warning, CheckCircle,
  FileText, Info
} from "@phosphor-icons/react";

const RISK_CONFIG = {
  fraud: { bg: "bg-red-50", border: "border-red-500", text: "text-red-900", label: "HIGH RISK — Fraud Detected", icon: XCircle, iconColor: "text-red-600", progressColor: "[&>div]:bg-red-600" },
  warning: { bg: "bg-yellow-50", border: "border-yellow-400", text: "text-yellow-900", label: "MEDIUM — Warnings Found", icon: Warning, iconColor: "text-yellow-600", progressColor: "[&>div]:bg-yellow-500" },
  clean: { bg: "bg-green-50", border: "border-green-400", text: "text-green-900", label: "CLEAN — No Fraud Indicators", icon: ShieldCheck, iconColor: "text-green-600", progressColor: "[&>div]:bg-green-500" },
};

export default function DocumentIntegrityTab({ claim }) {
  const docFraud = claim.document_fraud || {};
  const pages = claim.pages || [];
  const isProcessed = claim.status === "processed";
  const riskLevel = docFraud.risk_level || "clean";
  const config = RISK_CONFIG[riskLevel] || RISK_CONFIG.clean;
  const RiskIcon = config.icon;

  // Per-page fraud stats
  const suspiciousPages = pages.filter(p => p.fraud_indicators?.is_suspicious);
  const cleanPages = pages.filter(p => p.fraud_indicators && !p.fraud_indicators.is_suspicious);
  const uncheckedPages = pages.filter(p => !p.fraud_indicators);

  // Overall confidence score (0-100)
  const metadataScore = riskLevel === "fraud" ? 90 : riskLevel === "warning" ? 50 : 10;
  const pageScore = suspiciousPages.length > 0 ? Math.min(95, 60 + suspiciousPages.length * 10) : 5;
  const overallScore = Math.max(metadataScore, pageScore);

  const hasAnyData = Object.keys(docFraud).length > 0 || pages.length > 0;

  if (!hasAnyData) {
    return (
      <div className="p-6 text-center text-gray-400" data-testid="integrity-empty">
        <ShieldCheck size={48} className="mx-auto mb-3 text-gray-300" />
        <p className="text-sm">Upload and process a claim to view document integrity analysis.</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="document-integrity-tab">
      {/* ── Overall Risk Banner ── */}
      <div className={`border-2 rounded-md p-5 ${config.bg} ${config.border}`} data-testid="integrity-banner">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <RiskIcon size={28} weight="fill" className={config.iconColor} />
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider opacity-70">Document Integrity</p>
              <p className={`text-lg font-bold ${config.text}`}>{config.label}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs font-semibold uppercase tracking-wider opacity-70">Risk Score</p>
            <p className={`text-4xl font-black ${config.text}`} data-testid="integrity-score">{overallScore}</p>
            <p className="text-xs opacity-60">out of 100</p>
          </div>
        </div>
        <Progress value={overallScore} className={`h-3 ${config.progressColor}`} />
        <div className="flex justify-between mt-1.5 text-[10px] text-gray-400 font-medium">
          <span>0 — Clean</span>
          <span>30 — Low Risk</span>
          <span>60 — Medium</span>
          <span>80 — High Risk</span>
          <span>100</span>
        </div>
      </div>

      {/* ── Summary Cards ── */}
      <div className="grid grid-cols-4 gap-3">
        <div className="border border-gray-200 rounded-md p-3 text-center">
          <p className="label-xs mb-1">Total Pages</p>
          <p className="text-xl font-bold text-gray-900">{pages.length || claim.total_pages || 0}</p>
        </div>
        <div className={`border rounded-md p-3 text-center ${suspiciousPages.length > 0 ? "border-red-300 bg-red-50" : "border-gray-200"}`}>
          <p className="label-xs mb-1">Suspicious</p>
          <p className={`text-xl font-bold ${suspiciousPages.length > 0 ? "text-red-700" : "text-gray-900"}`}>{suspiciousPages.length}</p>
        </div>
        <div className="border border-green-200 bg-green-50 rounded-md p-3 text-center">
          <p className="label-xs mb-1">Clean Pages</p>
          <p className="text-xl font-bold text-green-700">{cleanPages.length}</p>
        </div>
        <div className="border border-gray-200 rounded-md p-3 text-center">
          <p className="label-xs mb-1">Metadata Flags</p>
          <p className={`text-xl font-bold ${(docFraud.flags_count || 0) > 0 ? "text-red-600" : "text-gray-900"}`}>{docFraud.flags_count || 0}</p>
        </div>
      </div>

      {/* ── PDF Metadata Analysis ── */}
      <section>
        <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <FileText size={18} className="text-[#002FA7]" />
          PDF Metadata Analysis
        </h3>

        {/* Metadata details card */}
        {docFraud.metadata && (
          <div className="border border-gray-200 rounded-md overflow-hidden mb-3">
            <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
              <p className="label-xs">File Properties</p>
            </div>
            <div className="p-4 grid grid-cols-2 gap-y-3 gap-x-6 text-sm">
              <div>
                <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider">Creator</p>
                <p className={`font-medium ${riskLevel === "fraud" ? "text-red-800 font-bold" : "text-gray-900"}`}>
                  {docFraud.metadata.creator || "—"}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider">Producer</p>
                <p className={`font-medium ${riskLevel === "fraud" ? "text-red-800 font-bold" : "text-gray-900"}`}>
                  {docFraud.metadata.producer || "—"}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider">Author</p>
                <p className="font-medium text-gray-900">{docFraud.metadata.author || "—"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider">Title</p>
                <p className="font-medium text-gray-900">{docFraud.metadata.title || "—"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider">Created</p>
                <p className="font-medium text-gray-900">{docFraud.metadata.creation_date || "—"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider">Modified</p>
                <p className={`font-medium ${docFraud.metadata.modification_date && docFraud.metadata.modification_date !== docFraud.metadata.creation_date ? "text-yellow-700 font-bold" : "text-gray-900"}`}>
                  {docFraud.metadata.modification_date || "—"}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider">PDF Version</p>
                <p className="font-medium text-gray-900">{docFraud.metadata.pdf_version || "—"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider">Encrypted</p>
                <p className="font-medium text-gray-900">{docFraud.metadata.is_encrypted ? "Yes" : "No"}</p>
              </div>
            </div>
          </div>
        )}

        {/* Metadata flags */}
        {(docFraud.flags || []).length > 0 && (
          <div className="space-y-2">
            {docFraud.flags.map((flag, idx) => {
              const isFraud = flag.severity === "fraud";
              const isWarning = flag.severity === "warning";
              const isClean = flag.severity === "clean";
              return (
                <div
                  key={`meta-flag-${idx}`}
                  className={`border rounded-md px-4 py-3 flex items-start gap-3 ${
                    isFraud ? "border-red-300 bg-red-50" :
                    isWarning ? "border-yellow-300 bg-yellow-50" :
                    "border-green-200 bg-green-50"
                  }`}
                  data-testid={`metadata-flag-${idx}`}
                >
                  {isFraud ? <XCircle size={18} weight="fill" className="text-red-600 mt-0.5 flex-shrink-0" /> :
                   isWarning ? <Warning size={18} weight="fill" className="text-yellow-600 mt-0.5 flex-shrink-0" /> :
                   <CheckCircle size={18} weight="fill" className="text-green-600 mt-0.5 flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-bold ${isFraud ? "text-red-900" : isWarning ? "text-yellow-900" : "text-green-800"}`}>
                      {flag.check}
                    </p>
                    <p className={`text-xs mt-0.5 ${isFraud ? "text-red-700" : isWarning ? "text-yellow-700" : "text-green-700"}`}>
                      {flag.detail}
                    </p>
                    {flag.value && flag.value !== "empty" && (
                      <p className="text-[10px] font-mono text-gray-500 mt-1">
                        {flag.field}: <span className={isFraud ? "text-red-600 font-bold" : ""}>{flag.value}</span>
                      </p>
                    )}
                  </div>
                  <Badge variant="outline" className={`text-[10px] flex-shrink-0 ${
                    isFraud ? "bg-red-100 text-red-800 border-red-300 font-bold" :
                    isWarning ? "bg-yellow-100 text-yellow-800 border-yellow-300" :
                    "bg-green-100 text-green-800 border-green-300"
                  }`}>
                    {flag.severity.toUpperCase()}
                  </Badge>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ── Per-Page AI Fraud Analysis ── */}
      {isProcessed && pages.length > 0 && (
        <section>
          <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <ShieldWarning size={18} className="text-[#002FA7]" />
            Per-Page AI Visual Analysis
          </h3>

          {suspiciousPages.length > 0 ? (
            <div className="space-y-2">
              {suspiciousPages.map(page => (
                <div
                  key={`page-fraud-${page.page_number}`}
                  className="border-2 border-red-400 bg-red-50 rounded-md px-4 py-3"
                  data-testid={`page-fraud-${page.page_number}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <XCircle size={16} weight="fill" className="text-red-600" />
                      <span className="text-sm font-bold text-red-900">
                        Page {page.page_number}
                      </span>
                      <Badge variant="outline" className="text-xs capitalize bg-white">
                        {(page.classification || "").replace(/_/g, " ")}
                      </Badge>
                    </div>
                    <Badge className="text-xs bg-red-600 text-white border-0 font-bold px-2">
                      {Math.round((page.fraud_indicators?.confidence || 0) * 100)}% Suspicious
                    </Badge>
                  </div>
                  <div className="bg-white border border-red-200 rounded-md p-3">
                    <p className="text-xs font-semibold text-red-800 mb-1.5">Findings:</p>
                    <ul className="space-y-1">
                      {(page.fraud_indicators?.findings || []).map((finding, fi) => (
                        <li key={`finding-${page.page_number}-${fi}`} className="text-xs text-red-700 flex items-start gap-2">
                          <span className="text-red-400 mt-0.5">•</span>
                          <span className="font-medium">{finding}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="border border-green-200 bg-green-50 rounded-md p-5 text-center">
              <ShieldCheck size={28} weight="fill" className="mx-auto text-green-600 mb-2" />
              <p className="text-sm font-medium text-green-800">All Pages Clean</p>
              <p className="text-xs text-green-600 mt-1">
                AI visual analysis found no signs of tampering, editing, or manipulation across {cleanPages.length > 0 ? cleanPages.length : pages.length} pages
              </p>
            </div>
          )}

          {/* Page-by-page summary table */}
          {isProcessed && pages.length > 0 && (
            <div className="mt-3 border border-gray-200 rounded-md overflow-hidden">
              <div className="bg-gray-50 px-3 py-1.5 border-b border-gray-200">
                <p className="label-xs">Page-by-Page Status</p>
              </div>
              <div className="max-h-48 overflow-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 sticky top-0">
                      <th className="px-3 py-1.5 text-left font-semibold text-gray-500">Page</th>
                      <th className="px-3 py-1.5 text-left font-semibold text-gray-500">Type</th>
                      <th className="px-3 py-1.5 text-center font-semibold text-gray-500">Status</th>
                      <th className="px-3 py-1.5 text-left font-semibold text-gray-500">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pages.map(page => {
                      const fi = page.fraud_indicators || {};
                      const isSus = fi.is_suspicious;
                      return (
                        <tr key={`row-${page.page_number}`} className={`border-b border-gray-100 ${isSus ? "bg-red-50" : ""}`}>
                          <td className="px-3 py-1.5 font-mono font-medium">P{page.page_number}</td>
                          <td className="px-3 py-1.5 capitalize text-gray-600">{(page.classification || "").replace(/_/g, " ")}</td>
                          <td className="px-3 py-1.5 text-center">
                            {isSus
                              ? <Badge className="text-[9px] bg-red-600 text-white border-0 font-bold">FRAUD</Badge>
                              : fi.confidence !== undefined
                                ? <Badge variant="outline" className="text-[9px] bg-green-50 text-green-700 border-green-200">CLEAN</Badge>
                                : <Badge variant="outline" className="text-[9px] text-gray-400">—</Badge>
                            }
                          </td>
                          <td className="px-3 py-1.5 text-gray-500 max-w-[200px] truncate">
                            {isSus ? (fi.findings || []).join("; ") : "No anomalies detected"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
